import {
  Building2,
  CalendarDays,
  Contact,
  LayoutDashboard,
  ListChecks,
  LucideIconData,
  Mail,
  Newspaper,
  Plug,
  Scale,
  Settings,
  ShieldCheck,
  Users,
  UserCog,
  Wallet,
  Webhook,
} from 'lucide-angular';

/** Subitem de um grupo expansível (ex.: Financeiro). */
export interface NavChild {
  label: string;
  route: string;
  permission: string;
  exact?: boolean;
}

/** Item do menu principal: link direto (`route`) ou grupo expansível (`children`). */
export interface NavItem {
  label: string;
  icon: LucideIconData;
  route?: string;
  permission?: string;
  exact?: boolean;
  /** Prefixo de URL que marca o grupo como ativo. */
  sectionPrefix?: string;
  children?: NavChild[];
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

/**
 * Fonte única do menu do escritório. Sidebar (desktop) e gaveta (celular) usam esta
 * mesma lista — novos módulos entram aqui, com a permissão correspondente da rota.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Rotina',
    items: [
      { label: 'Painel', icon: LayoutDashboard, route: '/app/dashboard', permission: 'dashboard.view' },
      { label: 'Publicações', icon: Newspaper, route: '/app/publications', permission: 'publications.view' },
      { label: 'Tarefas', icon: ListChecks, route: '/app/tasks', permission: 'tasks.view' },
    ],
  },
  {
    label: 'Carteira',
    items: [
      { label: 'Clientes', icon: Users, route: '/app/clients', permission: 'clients.view' },
      { label: 'Partes', icon: Contact, route: '/app/people', permission: 'people.view' },
      { label: 'Processos', icon: Scale, route: '/app/cases', permission: 'cases.view' },
    ],
  },
  {
    label: 'Gestão',
    items: [
      {
        label: 'Financeiro',
        icon: Wallet,
        sectionPrefix: '/app/financial',
        children: [
          { label: 'Visão geral', route: '/app/financial', permission: 'finance.overview.view', exact: true },
          { label: 'Cobranças', route: '/app/financial/payments', permission: 'finance.view' },
          { label: 'Assinaturas', route: '/app/financial/subscriptions', permission: 'finance.subscriptions.view' },
          { label: 'Relatório contábil', route: '/app/financial/reports/espelho-nfs', permission: 'finance.reports.view' },
        ],
      },
      {
        label: 'Escritório',
        icon: Building2,
        sectionPrefix: '/app/office',
        children: [
          { label: 'Perfil', route: '/app/settings/office-profile', permission: 'office_profile.view', exact: true },
          { label: 'Lançamentos', route: '/app/office/expenses', permission: 'office_expenses.view', exact: true },
          { label: 'Categorias de despesas', route: '/app/office/expenses/categories', permission: 'office_expense_categories.manage' },
        ],
      },
    ],
  },
  {
    label: 'Configurações',
    items: [
      { label: 'Visão geral', icon: Settings, route: '/app/settings', permission: 'settings.view', exact: true },
      { label: 'Integrações', icon: Plug, route: '/app/settings/integrations/asaas', permission: 'integrations.view' },
      {
        label: 'Google Agenda',
        icon: CalendarDays,
        route: '/app/settings/integrations/google-calendar',
        permission: 'integrations.google_calendar.view',
      },
      { label: 'Webhooks', icon: Webhook, route: '/app/settings/webhooks', permission: 'webhooks.view' },
      { label: 'Histórico de e-mails', icon: Mail, route: '/app/settings/email-deliveries', permission: 'email_deliveries.view' },
    ],
  },
  {
    label: 'Administração',
    items: [
      { label: 'Usuários', icon: UserCog, route: '/app/users', permission: 'users.view' },
      { label: 'Perfis e permissões', icon: ShieldCheck, route: '/app/roles', permission: 'roles.view' },
    ],
  },
];

/**
 * Filtra o menu pelas permissões do usuário: remove itens sem permissão, grupos sem
 * subitens visíveis e seções vazias.
 */
export function filterNavSections(
  sections: NavSection[],
  hasPermission: (permission: string) => boolean,
): NavSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: section.items
        .map((item) =>
          item.children
            ? { ...item, children: item.children.filter((child) => hasPermission(child.permission)) }
            : item,
        )
        .filter((item) =>
          item.children ? item.children.length > 0 : !item.permission || hasPermission(item.permission),
        ),
    }))
    .filter((section) => section.items.length > 0);
}
